import { Instagram, Facebook, MessageCircle } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export type SocialOrigin = "INSTAGRAM" | "FACEBOOK" | "WHATSAPP" | string;

/**
 * Returns the corresponding icon for a social media origin.
 */
export const getSocialIcon = (origin: SocialOrigin, className?: string): ReactNode => {
  if (!origin) return null;
  const normalizedOrigin = origin.toUpperCase();

  switch (normalizedOrigin) {
    case "INSTAGRAM":
      return <Instagram className={cn("text-pink-500", className)} />;
    case "FACEBOOK":
      return <Facebook className={cn("text-blue-600", className)} />;
    case "WHATSAPP":
      return <MessageCircle className={cn("text-green-500", className)} />;
    default:
      return null;
  }
};

/**
 * Returns a friendly name for the social media origin.
 */
export const getSocialName = (origin: SocialOrigin): string => {
  if (!origin) return "Unbekannt";
  const normalizedOrigin = origin.toUpperCase();

  switch (normalizedOrigin) {
    case "INSTAGRAM":
      return "Instagram";
    case "FACEBOOK":
      return "Facebook";
    case "WHATSAPP":
      return "WhatsApp";
    default:
      return origin;
  }
};

/**
 * Returns standard pill/chip color classes for a given origin.
 */
export const getSocialColorClasses = (origin: SocialOrigin): string => {
  if (!origin) return "bg-gray-50 text-gray-600 border-gray-100";
  const normalizedOrigin = origin.toUpperCase();
  switch (normalizedOrigin) {
    case "INSTAGRAM":
      return "bg-pink-50 text-pink-600 border-pink-100";
    case "FACEBOOK":
      return "bg-blue-50 text-blue-600 border-blue-100";
    case "WHATSAPP":
      return "bg-green-50 text-green-700 border-green-200";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

/**
 * Returns badge (icon box) color classes for a given origin.
 */
export const getSocialBadgeClasses = (origin: SocialOrigin): string => {
  if (!origin) return "bg-gray-50 border-gray-200 text-gray-500";
  const normalizedOrigin = origin.toUpperCase();
  switch (normalizedOrigin) {
    case "INSTAGRAM":
      return "bg-pink-50 border-pink-100/50 text-pink-600";
    case "FACEBOOK":
    case "META":
      return "bg-blue-50 border-blue-100/50 text-blue-600";
    case "WHATSAPP":
      return "bg-green-50 border-green-100 text-green-700";
    default:
      return "bg-gray-50 border-gray-200 text-gray-500";
  }
};
